import type { Room, SceneData, RoomLayout } from './types.ts';
import { classifyTypeFromCategoryName } from './classification.ts';

/**
 * Safely parses SceneData from a Room db object or SceneData directly.
 */
export function parseSceneData(roomOrScene: Room | SceneData): SceneData {
  if (roomOrScene && typeof roomOrScene === 'object' && 'sceneData' in roomOrScene) {
    return typeof roomOrScene.sceneData === 'string'
      ? JSON.parse(roomOrScene.sceneData)
      : roomOrScene.sceneData;
  }
  return roomOrScene as SceneData;
}

/**
 * Transforms external DB Room/SceneData into the internal/Python coordinate format (RoomLayoutDb).
 * Accepts an optional accessoryToCategoryMap (accessory UUID -> { categoryId, categoryName }) for precise classifications.
 */
export function mapSceneDataToRoomLayout(
  roomOrScene: Room | SceneData,
  accessoryToCategoryMap?: Map<string, { categoryId: string; categoryName: string }>
): RoomLayout {
  const sceneData = parseSceneData(roomOrScene);
  const firstRoom = sceneData._rooms[0];
  if (!firstRoom) {
    throw new Error('SceneData does not contain any room specifications.');
  }

  const roomWidth = firstRoom._size.x; // meters
  const roomHeight = firstRoom._size.y; // meters
  const roomLength = firstRoom._size.z; // meters

  const items = firstRoom._objects.map((obj) => {
    // Resolve exact category details from helper map
    const categoryInfo = accessoryToCategoryMap?.get(obj._accesoryId);

    // DB position is in meters, relative to room center (0,0,0)
    const x = obj._position.x + roomWidth / 2;
    const y = obj._position.z + roomLength / 2;
    const z = obj._position.y;

    // DB size is in millimeters
    const dimX = obj._size.x / 1000;
    const dimY = obj._size.z / 1000; // Z depth maps to Y length
    const dimZ = obj._size.y / 1000; // Y height maps to Z height

    // Convert rotation: DB can return Quaternions (with w) or Euler angles (without w)
    let angle = 0;
    const rot = obj._rotation as any;
    if (rot && rot.w !== undefined) {
      // It's a Quaternion, compute yaw (rotation around vertical Y-axis)
      const qx = rot.x || 0;
      const qy = rot.y || 0;
      const qz = rot.z || 0;
      const qw = rot.w;
      
      const yawRad = Math.atan2(2 * (qx * qz + qw * qy), 1 - 2 * (qy * qy + qz * qz));
      angle = (yawRad * 180 / Math.PI) % 360;
    } else {
      // Fallback: It's Euler angles in radians around Y-axis
      angle = (rot.y * 180 / Math.PI) % 360;
    }
    if (angle < 0) {
      angle += 360;
    }

    // Resolve strict description, colors, materials
    const description = (obj as any).description || categoryInfo?.categoryName || obj._name;
    const colors = (obj as any).colors || [];
    const materials = (obj as any).materials || obj._materials?.map((m: any) => m._variant).filter(Boolean) || [];

    return {
      itemId: obj._id.toString(),
      label: obj._name,
      description,
      type: classifyTypeFromCategoryName(categoryInfo?.categoryName, obj._name),
      position: { x, y, z },
      size: {
        width: dimX,
        length: dimY,
        height: dimZ
      },
      angle,
      colors,
      materials,
      metadata: {
        originalId: obj._id,
        parentId: obj._parentId,
        accesoryId: obj._accesoryId,
        categoryId: categoryInfo?.categoryId, // preserve exact category UUID resolved from DB
        url: obj._url,
        snapType: obj._snapType,
        materials: obj._materials,
        _originalSize: obj._size,
        _originalRotation: obj._rotation,
        ...(obj as any) // Preserve any enhanced/custom properties present in the source database object
      }
    };
  });

  // Extract ID and name from DB level if available
  const roomId = ('id' in roomOrScene) ? (roomOrScene as Room).id : 'scene-room';
  const roomName = ('name' in roomOrScene) ? (roomOrScene as Room).name : (sceneData._name || 'Room Layout');

  return {
    id: roomId,
    name: roomName,
    width: roomWidth,
    length: roomLength,
    height: roomHeight,
    items,
    metadata: {
      originalGroups: sceneData._groups,
      originalMaterials: sceneData._materials,
      originalWalls: firstRoom._walls,
      originalFloor: firstRoom._floor,
      originalVersion: sceneData._version
    }
  };
}

/**
 * Updates a DB SceneData object with the computed layout coordinates from RoomLayoutDb.
 */
export function mapRoomLayoutToSceneData(
  roomDb: RoomLayout,
  originalSceneData: SceneData
): SceneData {
  const sceneData = JSON.parse(JSON.stringify(originalSceneData)) as SceneData;
  const firstRoom = sceneData._rooms[0];
  if (!firstRoom) {
    throw new Error('SceneData does not contain any rooms.');
  }

  const roomWidth = firstRoom._size.x;
  const roomLength = firstRoom._size.z;

  const dbItemMap = new Map(roomDb.items.map(item => [item.itemId, item]));

  firstRoom._objects = firstRoom._objects.map((obj) => {
    const updated = dbItemMap.get(obj._id.toString());
    if (updated) {
      const x = updated.position.x - roomWidth / 2;
      const z = updated.position.y - roomLength / 2;
      const y = updated.position.z;

      const yaw = updated.angle * Math.PI / 180;
      let rotation: any;
      if (obj._rotation && (obj._rotation as any).w !== undefined) {
        rotation = {
          x: 0,
          y: Math.sin(yaw / 2),
          z: 0,
          w: Math.cos(yaw / 2)
        };
      } else {
        rotation = {
          x: obj._rotation.x,
          y: yaw,
          z: obj._rotation.z
        };
      }

      return {
        ...obj,
        _position: { x, y, z },
        _rotation: rotation
      };
    }
    return obj;
  });

  return sceneData;
}

/**
 * Reconstructs a full SceneData object from a RoomLayoutDb object, utilizing
 * the preserved metadata and items mapping.
 */
export function reconstructSceneDataFromRoomLayout(roomDb: RoomLayout): SceneData {
  const reconstructedObjects = roomDb.items.map((item, index) => {
    const meta = item.metadata || {};
    const x = item.position.x - roomDb.width / 2;
    const z = item.position.y - roomDb.length / 2;
    const y = item.position.z;
    const yaw = item.angle * Math.PI / 180;
    const originalRot = meta._originalRotation || {};
    let rotation: any;
    const isNewItem = typeof meta.originalId !== 'number' || Number.isNaN(meta.originalId);
    if (isNewItem || (originalRot && originalRot.w !== undefined)) {
      rotation = {
        x: 0,
        y: Math.sin(yaw / 2),
        z: 0,
        w: Math.cos(yaw / 2)
      };
    } else {
      rotation = {
        x: originalRot.x ?? 0,
        y: yaw,
        z: originalRot.z ?? 0
      };
    }

    let idVal = typeof meta.originalId === 'number' && !Number.isNaN(meta.originalId) ? meta.originalId : NaN;
    if (Number.isNaN(idVal)) {
      idVal = Number.parseInt(item.itemId, 10);
    }
    if (Number.isNaN(idVal)) {
      idVal = 1000 + index;
    }

    let parentIdVal = typeof meta.parentId === 'number' && !Number.isNaN(meta.parentId) ? meta.parentId : NaN;
    if (Number.isNaN(parentIdVal) || parentIdVal === 0) {
      parentIdVal = -1; // Floor component parent ID is -1, 0 is invalid and hides the object
    }

    return {
      _id: idVal,
      _accesoryId: meta.accesoryId ?? "",
      _parentId: parentIdVal,
      _name: item.label,
      _position: { x, y, z },
      _rotation: rotation,
      _size: meta._originalSize ?? {
        x: item.size.width * 1000,
        y: item.size.height * 1000,
        z: item.size.length * 1000
      },
      _url: (meta.url && /\.(glb|gbl)(\?.*)?$/i.test(meta.url)) ? meta.url : "",
      _snapType: meta.snapType ?? 1,
      _materials: meta.materials ?? []
    };
  });

  const rootMeta = roomDb.metadata || {};
  return {
    _version: rootMeta.originalVersion || 2,
    _name: roomDb.name || 'Moravio PDG Layout',
    _rooms: [
      {
        _size: { x: roomDb.width, y: roomDb.height, z: roomDb.length },
        _position: { x: 0, y: 0, z: 0 },
        _rotation: { x: 0, y: 0, z: 0 },
        _objects: reconstructedObjects,
        _floor: rootMeta.originalFloor || "Parquet",
        _walls: rootMeta.originalWalls || {
          _leftWall: { r: 255, g: 255, b: 255, a: 1 },
          _rightWall: { r: 255, g: 255, b: 255, a: 1 },
          _frontWall: { r: 255, g: 255, b: 255, a: 1 },
          _backWall: { r: 255, g: 255, b: 255, a: 1 },
          _ceiling: { r: 255, g: 255, b: 255, a: 1 }
        }
      }
    ],
    _groups: rootMeta.originalGroups || [],
    _materials: rootMeta.originalMaterials || []
  };
}
